import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AiChatOption, AiChatSelectValue } from '@app/shared/models/agent-chat.models';
import { AiChatCustomValueComponent } from '../ai-chat-custom-value/ai-chat-custom-value.component';
import { AiChatGateActionsBase } from '../ai-chat-gate-actions-base';

@Component({
  selector: 'fastr-ui-ai-chat-multi-select',
  templateUrl: './ai-chat-multi-select.component.html',
  styleUrls: ['./ai-chat-multi-select.component.scss'],
})
export class AiChatMultiSelectComponent
  extends AiChatGateActionsBase
  implements OnChanges, OnDestroy
{
  @Input() question = '';
  /** False once the question has already been streamed into the reply bubble. */
  @Input() animateQuestion = true;
  @Input() options: AiChatOption[] = [];
  @Input() allowCustomValue = true;
  @Input() customValuePlaceholder = 'Type your own';
  @Input() showVoiceIcon = true;
  @Input() requireSelection = true;
  // 0 = uncapped: the harness sends no max_select and validates counts itself, so a cap
  // here would silently refuse valid answers on gathering gates that offer up to six.
  @Input() maxSelections = 0;

  @Output() submitted = new EventEmitter<AiChatSelectValue>();

  selectedIds = new Set<string | number>();
  customValue = '';

  @ViewChild(AiChatCustomValueComponent) private customValueInput?: AiChatCustomValueComponent;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.selectedIds = new Set(
        this.options.filter((option) => option.selected).map((option) => option.id),
      );
    }
  }

  ngOnDestroy(): void {
    this.stopVoiceDictation();
  }

  toggleOption(option: AiChatOption): void {
    if (this.isOptionDisabled(option)) {
      return;
    }

    if (this.selectedIds.has(option.id)) {
      this.selectedIds.delete(option.id);
    } else {
      this.selectedIds.add(option.id);
    }
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.stopVoiceDictation();
    this.submitted.emit(this.getValue());
  }

  isSelected(option: AiChatOption): boolean {
    return this.selectedIds.has(option.id);
  }

  isOptionDisabled(option: AiChatOption): boolean {
    return (
      option.disabled ||
      (!this.isSelected(option) &&
        this.maxSelections > 0 &&
        this.selectedIds.size >= this.maxSelections)
    );
  }

  canSubmit(): boolean {
    return !this.requireSelection || this.hasAnyValue();
  }

  trackByOptionId(_index: number, option: AiChatOption): string | number {
    return option.id;
  }

  protected override stopVoiceDictation(): void {
    this.customValueInput?.stop();
  }

  protected override hasAnyValue(): boolean {
    return this.selectedIds.size > 0 || this.customValue.trim().length > 0;
  }

  private getValue(): AiChatSelectValue {
    return {
      selectedOptions: this.options.filter((option) => this.selectedIds.has(option.id)),
      customValue: this.customValue.trim() || undefined,
    };
  }
}
